import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  faUser, faClock, faBell, faCheck, faPaperPlane
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AlertModal } from '../components/ConfirmModal'
import api from '../utils/api'

function Settings() {
  const { user, updateUser } = useAuth()
  const [settings, setSettings] = useState({
    work_start: '09:15',
    daily_hours: 8,
    remind_morning: true,
    remind_late: true,
    remind_exit: true,
    remind_report: true,
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    title?: string
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  useEffect(() => {
    if (user) {
      setSettings(prev => ({
        ...prev,
        work_start: user.work_start || '09:15',
        daily_hours: user.daily_hours || 8,
      }))
    }
  }, [user])

  // Try linking telegram_id if opened inside telegram WebApp
  useEffect(() => {
    const tgId = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.id
    if (tgId && user && user.telegram_id !== tgId) {
      api.post('/auth/link-telegram', { telegram_id: tgId })
        .then(() => {
          updateUser({ telegram_id: tgId })
        })
        .catch(() => {})
    }
  }, [user])

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateUser({
        work_start: settings.work_start,
        daily_hours: settings.daily_hours,
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ذخیره شد',
        message: 'تنظیمات با موفقیت ذخیره گردید.'
      })
    } catch (error: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: error.response?.data?.detail || 'خطا در ذخیره تنظیمات'
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4 max-w-xl mx-auto pb-20">
      {/* Work Settings */}
      <div className="card p-5 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">
          <FontAwesomeIcon icon={faClock} className="text-emerald-500" />
          تنظیمات ساعت کاری
        </h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">ساعت شروع کار مقرر:</label>
            <input
              type="time"
              value={settings.work_start}
              onChange={(e) => setSettings(prev => ({ ...prev, work_start: e.target.value }))}
              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">ساعت کارکرد روزانه:</label>
            <select
              value={settings.daily_hours}
              onChange={(e) => setSettings(prev => ({ ...prev, daily_hours: parseFloat(e.target.value) }))}
              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {[4, 5, 6, 7, 7.5, 8, 8.5, 9, 10].map(h => (
                <option key={h} value={h} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{h} ساعت</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="card p-5 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">
          <FontAwesomeIcon icon={faBell} className="text-amber-500" />
          تنظیمات اعلان‌ها و یادآورها
        </h2>
        <div className="space-y-3">
          <label className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700/40 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
            <div>
              <span className="text-xs font-bold text-gray-800 dark:text-gray-200">☀️ یادآوری صبحگاهی (ساعت ۰۷:۳۰)</span>
              <p className="text-[11px] text-gray-400">یادآوری ثبت ورود در روزهای کاری</p>
            </div>
            <input
              type="checkbox"
              checked={settings.remind_morning}
              onChange={(e) => setSettings(prev => ({ ...prev, remind_morning: e.target.checked }))}
              className="w-4 h-4 text-emerald-600 rounded"
            />
          </label>
          
          <label className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700/40 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
            <div>
              <span className="text-xs font-bold text-gray-800 dark:text-gray-200">⚠️ هشدار تاخیر ورود (ساعت ۰۹:۲۰)</span>
              <p className="text-[11px] text-gray-400">هشدار تاخیر و ایجاد خودکار تسک فرم تاخیر</p>
            </div>
            <input
              type="checkbox"
              checked={settings.remind_late}
              onChange={(e) => setSettings(prev => ({ ...prev, remind_late: e.target.checked }))}
              className="w-4 h-4 text-emerald-600 rounded"
            />
          </label>

          <label className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700/40 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
            <div>
              <span className="text-xs font-bold text-gray-800 dark:text-gray-200">⏰ یادآوری پایان کار و ثبت خروج (ساعت ۱۷:۱۵)</span>
              <p className="text-[11px] text-gray-400">یادآوری ثبت خروج هنگام پایان شیفت کاری</p>
            </div>
            <input
              type="checkbox"
              checked={settings.remind_exit}
              onChange={(e) => setSettings(prev => ({ ...prev, remind_exit: e.target.checked }))}
              className="w-4 h-4 text-emerald-600 rounded"
            />
          </label>
          
          <label className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700/40 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
            <div>
              <span className="text-xs font-bold text-gray-800 dark:text-gray-200">📊 گزارش ماهانه (اول هر ماه ساعت ۰۹:۰۰)</span>
              <p className="text-[11px] text-gray-400">اعلان آماده شدن گزارش کارکرد ماه گذشته</p>
            </div>
            <input
              type="checkbox"
              checked={settings.remind_report}
              onChange={(e) => setSettings(prev => ({ ...prev, remind_report: e.target.checked }))}
              className="w-4 h-4 text-emerald-600 rounded"
            />
          </label>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold shadow-md shadow-emerald-500/20 transition-all flex items-center justify-center gap-2"
      >
        {saving ? 'در حال ذخیره...' : saved ? '✓ تغییرات با موفقیت ذخیره شد' : 'ذخیره تغییرات'}
        {saved && <FontAwesomeIcon icon={faCheck} />}
      </button>

      {/* Telegram Connection Card */}
      <div className="card p-5 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
          <FontAwesomeIcon icon={faPaperPlane} className="text-blue-500" />
          اتصال ربات تلگرام
        </h2>
        {user?.telegram_id ? (
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/40 rounded-xl flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
                ✅ حساب شما به ربات تلگرام متصل است
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                شناسه تلگرام: <span className="font-mono font-bold">{user.telegram_id}</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/40 rounded-xl space-y-2">
            <p className="text-xs font-bold text-amber-700 dark:text-amber-300">
              ⚠️ حساب تلگرام متصل نیست
            </p>
            <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">
              برای دریافت اعلان‌ها و ثبت تردد سریع در تلگرام، وارد ربات تلگرام شده و دستور زیر را ارسال کنید:
            </p>
            <div className="p-2 bg-white dark:bg-slate-900 rounded-lg border border-amber-200 dark:border-amber-800 font-mono text-xs font-bold text-slate-800 dark:text-slate-200 text-left select-all" dir="ltr">
              /login {user?.username || 'username'} password
            </div>
          </div>
        )}
      </div>

      {/* Account Info */}
      <div className="card p-5 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
          <FontAwesomeIcon icon={faUser} className="text-purple-500" />
          اطلاعات کاربر
        </h2>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
            <span className="text-gray-400">نام کاربر:</span>
            <span className="font-bold text-gray-800 dark:text-gray-200">{user?.name || '-'}</span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
            <span className="text-gray-400">نام کاربری:</span>
            <span className="font-bold text-gray-800 dark:text-gray-200">{user?.username || '-'}</span>
          </div>
          <div className="flex justify-between py-1.5">
            <span className="text-gray-400">نقش کاربری:</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400">{user?.role === 'admin' ? 'مدیر سیستم' : 'کاربر'}</span>
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Settings
